import { create } from 'zustand';
import type { WorkListQuery, WorkListResponse, WorkSummary } from '@shared/work-items/contracts';
import { acknowledgeWorkItems, getWorkItems, getWorkSummary } from '@/services/work-items/api';

type Store = {
  context: string | null;
  generation: number;
  summary: WorkSummary | null;
  summaryLoading: boolean;
  summaryError: boolean;
  list: WorkListResponse | null;
  listKey: string | null;
  listLoading: boolean;
  listError: boolean;
  acknowledgementLoading: boolean;
  acknowledgementError: boolean;
};

const empty: Omit<Store, 'context' | 'generation'> = {
  summary: null,
  summaryLoading: false,
  summaryError: false,
  list: null,
  listKey: null,
  listLoading: false,
  listError: false,
  acknowledgementLoading: false,
  acknowledgementError: false,
};

export const useWorkItemsStore = create<Store>(() => ({ ...empty, context: null, generation: 0 }));
let summaryRequest: { generation: number; promise: Promise<void> } | null = null;
const listRequests = new Map<string, { generation: number; promise: Promise<void> }>();
let listSequence = 0;

/** Context includes account, authorization revision, and session identity. Never persisted. */
export function setWorkItemsContext(context: string | null) {
  if (useWorkItemsStore.getState().context === context) return;
  useWorkItemsStore.setState((state) => ({
    ...empty,
    context,
    generation: state.generation + 1,
  }));
  summaryRequest = null;
  listRequests.clear();
  listSequence += 1;
}

function invalidateRequests() {
  useWorkItemsStore.setState((state) => ({
    generation: state.generation + 1,
    summaryLoading: false,
    listLoading: false,
  }));
  summaryRequest = null;
  listRequests.clear();
  listSequence += 1;
}

export function refreshWorkSummary(): Promise<void> {
  const { context, generation } = useWorkItemsStore.getState();
  if (!context) return Promise.resolve();
  if (summaryRequest?.generation === generation) return summaryRequest.promise;
  useWorkItemsStore.setState({ summaryLoading: true, summaryError: false });
  const promise = getWorkSummary()
    .then((summary) => {
      const state = useWorkItemsStore.getState();
      if (state.generation !== generation) return;
      useWorkItemsStore.setState({ summary, summaryError: false });
      if (summary.enabled && state.summary?.revision !== summary.revision && state.listKey) {
        void refreshWorkItems(JSON.parse(state.listKey));
      }
    })
    .catch(() => {
      if (useWorkItemsStore.getState().generation === generation) {
        useWorkItemsStore.setState({ summaryError: true });
      }
    })
    .finally(() => {
      if (useWorkItemsStore.getState().generation === generation) {
        useWorkItemsStore.setState({ summaryLoading: false });
      }
      if (summaryRequest?.promise === promise) summaryRequest = null;
    });
  summaryRequest = { generation, promise };
  return promise;
}

export function refreshWorkItems(query: WorkListQuery): Promise<void> {
  const { context, generation } = useWorkItemsStore.getState();
  if (!context) return Promise.resolve();
  const key = JSON.stringify(query);
  const existing = listRequests.get(key);
  if (existing?.generation === generation && useWorkItemsStore.getState().listKey === key)
    return existing.promise;
  const sequence = ++listSequence;
  useWorkItemsStore.setState({ listKey: key, list: null, listLoading: true, listError: false });
  const promise = getWorkItems(query)
    .then((list) => {
      const state = useWorkItemsStore.getState();
      if (state.generation !== generation || state.listKey !== key || listSequence !== sequence)
        return;
      useWorkItemsStore.setState({ list, listError: false });
    })
    .catch(() => {
      const state = useWorkItemsStore.getState();
      if (state.generation === generation && state.listKey === key && listSequence === sequence) {
        useWorkItemsStore.setState({ listError: true });
      }
    })
    .finally(() => {
      const state = useWorkItemsStore.getState();
      if (state.generation === generation && state.listKey === key && listSequence === sequence) {
        useWorkItemsStore.setState({ listLoading: false });
      }
      if (listRequests.get(key)?.promise === promise) listRequests.delete(key);
    });
  listRequests.set(key, { generation, promise });
  return promise;
}

/** Confirms only REVIEW items from the currently rendered list. */
export async function acknowledgeDisplayedReviews(): Promise<void> {
  const state = useWorkItemsStore.getState();
  if (!state.context || !state.list?.enabled || state.acknowledgementLoading) return;
  const items = state.list.items
    .filter((item) => item.kind === 'review')
    .map((item) => ({ id: item.id, version: item.version }));
  if (!items.length) return;
  useWorkItemsStore.setState({ acknowledgementLoading: true, acknowledgementError: false });
  try {
    await acknowledgeWorkItems(items);
    if (useWorkItemsStore.getState().generation !== state.generation) return;
    // Invalidate responses started before the acknowledgement was accepted. A newer
    // review version stays visible if the server returns it in the next list.
    invalidateRequests();
    const activeListKey = useWorkItemsStore.getState().listKey;
    useWorkItemsStore.setState({ list: null });
    await Promise.all([
      refreshWorkSummary(),
      activeListKey ? refreshWorkItems(JSON.parse(activeListKey)) : Promise.resolve(),
    ]);
  } catch {
    if (useWorkItemsStore.getState().context === state.context) {
      useWorkItemsStore.setState({ acknowledgementError: true });
    }
  } finally {
    if (useWorkItemsStore.getState().context === state.context) {
      useWorkItemsStore.setState({ acknowledgementLoading: false });
    }
  }
}

/** A source detail has loaded successfully; confirm only the version it displayed. */
export async function acknowledgeReviewForSource(
  pageKey: string,
  sourceId: string,
  meaningfulVersion: string,
): Promise<void> {
  const state = useWorkItemsStore.getState();
  if (!state.context) return;
  const result = await getWorkItems({ pageKey, sourceId, kind: 'review', pageSize: 100 });
  if (useWorkItemsStore.getState().generation !== state.generation) return;
  if (!result.enabled || !result.items.length) return;
  const acknowledgements = result.items
    .filter((item) => item.kind === 'review' && item.meaningfulVersion === meaningfulVersion)
    .map((item) => ({ id: item.id, version: item.version }));
  if (!acknowledgements.length) return;
  await acknowledgeWorkItems(acknowledgements);
  if (useWorkItemsStore.getState().generation === state.generation)
    await refreshWorkItemsAfterOperation();
}

/** Call after a successful operation on a source page. */
export function refreshWorkItemsAfterOperation(): Promise<void> {
  const state = useWorkItemsStore.getState();
  if (!state.context) return Promise.resolve();
  invalidateRequests();
  return Promise.all([
    refreshWorkSummary(),
    state.listKey ? refreshWorkItems(JSON.parse(state.listKey)) : Promise.resolve(),
  ]).then(() => undefined);
}
